import React from 'react';
import { useForm } from 'react-hook-form';
import { Box, Dialog, HStack, Portal, Text, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { useCreateContact, useExternalUsersVerify } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { countryCode, yup } from 'utils';

type XMModalType = { onClose: () => void; isOpen: boolean };

const XMModal: React.FC<XMModalType> = ({ isOpen, onClose }: XMModalType) => {
  const { t } = useTranslation('component.dashboard');

  const [selectedCountry, setSelectedCountry] = React.useState<string>('');

  const closeHandler = () => onClose();
  const { mutateAsync: verify } = useExternalUsersVerify();
  const { mutateAsync: createContact, isPending: createLoading } =
    useCreateContact();

  const schema = yup.object().shape({
    country: yup.string().label('country').required(),
    identity: yup.string().label('identity').required(),
  });

  const selectedFlag = countryCode.find(
    (item) => item.value === selectedCountry
  )?.flagSvg;

  const {
    register,
    resetField,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const formProps = useInputAttributes(register, errors, {
    country: t('entity.country'),
    identity: t('entity.identity'),
  });

  const acceptHandler = () => {
    verify({ system_identifier: 'XM' }).then(() => {
      closeHandler();
    });
  };

  const onSubmit = async (data: { identity: string; country: string }) => {
    createContact({
      identity: data.country + data.identity.replace(data.country || '', ''),
      type: 'MOBILE',
    }).then(() => {
      acceptHandler();
    });
  };

  return (
    <Dialog.Root
      size={{ base: 'xl', md: 'md' }}
      open={isOpen}
      placement="center"
      closeOnInteractOutside={false}
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) {
          closeHandler();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content {...serviceDialogShellProps()}>
            <ServiceDialogHeader
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <WarningIcon />
            </ServiceDialogHeader>
            <ServiceDialogCloseTrigger />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ServiceDialogBody fontSize="md">
                <VStack w="100%" py={4}>
                  <Box w="100%">
                    <Text w="100%" textAlign="left" pb={4}>
                      {t('XMDesc')}
                    </Text>
                    <Input
                      {...formProps.country}
                      isSelectBox
                      options={countryCode}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setSelectedCountry(e.target.value);
                        resetField('identity');
                      }}
                      leftValue={
                        selectedFlag ? (
                          <img
                            src={selectedFlag}
                            alt="-"
                            width="20px"
                            height="20px"
                          />
                        ) : (
                          ''
                        )
                      }
                    />
                  </Box>
                  <Box w="100%">
                    <Input
                      {...formProps.identity}
                      leftValue={selectedCountry}
                    />
                  </Box>
                </VStack>
                <Box fontSize="sm" color="body2">
                  {t('XMAccess')}
                </Box>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <HStack justifyContent="flex-end">
                  <Button
                    variant="filled"
                    title="Submit"
                    type="submit"
                    isLoading={createLoading}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                </HStack>
              </ServiceDialogFooter>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
export default XMModal;
